import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Fingerprint, ArrowRight, GrapeIcon } from "lucide-react";

const VINEYARD_IMG =
  "https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?auto=format&fit=crop&w=2400&q=80";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("team");
  const logoUrl = localStorage.getItem("vineops_logo") || "/vineops-logo-v2.png";

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await login(username.trim(), password);
    setBusy(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  const biometric = () => {
    if (!window.PublicKeyCredential) {
      setError("Biometric not supported on this device.");
      return;
    }
    setError("Sign in once with password to enable Face ID / Touch ID.");
  };

  return (
    <div
      className="min-h-screen flex bg-[#F8F6F2]"
      style={{ fontFamily: "'Inter Tight', system-ui, sans-serif" }}
    >
      {/* Left: vineyard photography */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <img
          src={VINEYARD_IMG}
          alt="Napa Valley vineyard at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1F3D34]/70 via-[#1F3D34]/30 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="VineOps by Vinedresser" className="h-40 w-auto object-contain" />
            <div className="hidden">
              <div className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>VineOps</div>
              <div className="text-[11px] tracking-wide uppercase text-white/70 -mt-1">by Vinedresser</div>
            </div>
          </div>
          <div className="max-w-md">
            <h2 className="text-5xl leading-[1.05] font-medium" style={{ fontFamily: "'Fraunces', serif" }}>
              Vineyard operations,<br /><em className="text-[#D8C7A1] not-italic">all in one place.</em>
            </h2>
            <p className="mt-4 text-white/80 text-base">
              Built for vineyard management companies and California farm labor contractors. From bud break to harvest.
            </p>
          </div>
          <div className="text-[11px] text-white/60 tracking-wide">
            © {new Date().getFullYear()} Vinedresser Vineyard Management, INC.
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-center gap-3 mb-10 lg:hidden">
            <img src={logoUrl} alt="VineOps by Vinedresser" className="h-32 w-auto object-contain mx-auto" />
          </div>

          <h1 className="text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Fraunces', serif", color: "#1F3D34" }}>
            Welcome back
          </h1>
          <div className="flex gap-2 mb-4">
            <button type="button" data-testid="login-mode-team" onClick={() => setMode("team")}
              className={`h-10 px-4 text-xs font-bold uppercase border flex-1 ${mode === "team" ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white text-stone-600 border-stone-200"}`}>
              Vineyard Team
            </button>
            <button type="button" data-testid="login-mode-client" onClick={() => setMode("client")}
              className={`h-10 px-4 text-xs font-bold uppercase border flex-1 ${mode === "client" ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white text-stone-600 border-stone-200"}`}>
              Client / Owner
            </button>
          </div>
          <p className="text-stone-500 text-sm mb-6" data-testid="login-subtitle">
            {mode === "client"
              ? "Sign in to view your blocks, crop estimates, scouting reports and spray records."
              : "Sign in to manage your crews and timecards."}
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-[11px] font-medium tracking-wide uppercase text-stone-500 mb-1.5 block">Username</label>
              <input
                data-testid="login-email-input"
                type="text"
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full h-12 px-4 bg-white border border-stone-200 text-[15px]"
              />
            </div>
            <div>
              <label className="text-[11px] font-medium tracking-wide uppercase text-stone-500 mb-1.5 block">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full h-12 px-4 bg-white border border-stone-200 text-[15px]"
              />
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-xl border border-red-200 bg-red-50 text-red-700 p-3 text-sm">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-btn"
              disabled={busy}
              type="submit"
              className="w-full h-12 bg-[#1F3D34] text-white text-[15px] font-medium shadow-sm shadow-[#1F3D34]/20 hover:bg-[#27483D] flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"} <ArrowRight size={18} strokeWidth={2} />
            </button>
            {mode === "team" && (
            <button
              data-testid="login-biometric-btn"
              type="button"
              onClick={biometric}
              className="w-full h-12 bg-white text-[#1F3D34] text-[15px] font-medium border border-stone-200 hover:bg-stone-50 flex items-center justify-center gap-2"
            >
              <Fingerprint size={20} strokeWidth={1.8} /> Face ID / Touch ID
            </button>
            )}
          </form>

          {mode !== "team" && (
          <div className="mt-10 pt-5 border-t border-stone-200 text-xs text-stone-500" data-testid="client-login-help">
            No login yet? Contact your vineyard manager to get portal access for your ranch.
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
